/// <amd-module name="WIT/ProcessTemplateSelector/SimpleComboBox" />
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Location } from "VSSUI/Utilities/Position";

export interface IHelpIconProps {
    tooltip: string;
    ariaLabel?: string;
    href?: string;
}

export interface IOptionItem {
    text: string;
    key: string;
    children?: IChildOptionItem[];
}

export interface IChildOptionItem {
    text: string;
    key: string;
}

export interface ISimpleComboBoxProps {
    sourceItems: IOptionItem[];
    defaultSelectedKey: string;
    onOptionChanged: (key: string) => void;
    disabled?: boolean;
    ariaLabel?: string;
    className?: string;
    id?: string;
    label?: string;
    helpIconProps?: IHelpIconProps;
    calloutClassName?: string;
}

export interface ISimpleComboBoxState {
    dropdownOptions: IListBoxItem[];
    isExpanded: { [key: string]: boolean };
    selectedKey: string | number;
}

export class SimpleComboBox extends VssComponent<ISimpleComboBoxProps, ISimpleComboBoxState> {
    private _dropdownId: string;
    private _helpTooltipId: string;
    private _comboBoxSelection = new DropdownSelection();

    constructor(props: ISimpleComboBoxProps, context: IVssComponentContext) {
        super(props, context);

        this._dropdownId = this.props.id || getId("drop-down");
        this._helpTooltipId = getId("help-tooltip");

        const isExpanded: { [key: string]: boolean } = this._getAllExpandedDictionary();
        const dropdownOptions: IListBoxItem[] = this._getDropdownOptions(isExpanded);

        const defaultSelectedOption: IListBoxItem | undefined = dropdownOptions.find(option => option.id === this.props.defaultSelectedKey);
        const firstOptionKey: string | number = dropdownOptions[0] ? dropdownOptions[0].id : "";
        const selectedKey: string | number = defaultSelectedOption ? defaultSelectedOption.id : firstOptionKey;

        let indexOfSelectedKey: number = selectedKey === "" ? 0 : dropdownOptions.findIndex(option => option.id === selectedKey); 
        this._comboBoxSelection.select(indexOfSelectedKey);

        this.state = {
            dropdownOptions: dropdownOptions,
            isExpanded: isExpanded,
            selectedKey: selectedKey
        };
    }

    /**
     * Render the container with the given classname which wrap's the web access combo control
     */
    public render(): JSX.Element {
        return (
            <div>
                <div className="flex flex-center">
                    <FormItem label={this.props.label} className="dropdown-label">
                        {this.props.helpIconProps && (
                            <Link
                                className="help-icon-container"
                                ariaLabel={this.props.helpIconProps.ariaLabel}
                                ariaDescribedBy={this._helpTooltipId}
                                href={this.props.helpIconProps.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={this.props.disabled}
                                tooltipProps={{
                                    anchorOffset: { horizontal: 0, vertical: -10 },
                                    anchorOrigin: { horizontal: Location.center, vertical: Location.start },
                                    id: this._helpTooltipId,
                                    showOnFocus: true,
                                    text: this.props.helpIconProps.tooltip,
                                    tooltipOrigin: { horizontal: Location.center, vertical: Location.end }
                                }}
                            >
                                <Icon iconName="Unknown" />
                            </Link>
                        )}

                    </FormItem>
                </div>
                <Dropdown
                    className={this.props.className}
                    ariaLabel={this.props.ariaLabel}
                    disabled={this.props.disabled}
                    selection={this._comboBoxSelection}
                    onSelect={this._onChanged}
                    items={this.state.dropdownOptions}
                    calloutContentClassName={css("dropdown-callout", this.props.calloutClassName)}
                />
            </div>
        );
    }

    private _onChanged = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, option: IListBoxItem): void => {
        this.props.onOptionChanged(option.id.toString());
        this.setState({ selectedKey: option.id });
    };

    private _getAllExpandedDictionary = (): { [key: string]: boolean } => {
        const isExpanded: { [key: string]: boolean } = {};
        this.props.sourceItems.map((item, index) => {
            const children = item.children || [];
            const hasInheritedItems = children.length > 0;
            if (item.key && hasInheritedItems) {
                isExpanded[item.key] = true;
            }
        });

        return isExpanded;
    };

    private _getDropdownOptions = (isExpandedDictionary: { [key: string]: boolean }): IListBoxItem[] => {
        const dropdownOptions: IListBoxItem[] = [];
        this.props.sourceItems.map((item, index) => {
            if (item.key && item.text) {
                const children = item.children || [];
                const hasInheritedItems = children.length > 0;
                const isExpanded = isExpandedDictionary[item.key];
                dropdownOptions.push({
                    id: item.key,
                    text: item.text,
                    data: {
                        isInheritedItem: false,
                        hasInheritedItems: hasInheritedItems,
                        isExpanded: isExpanded
                    }
                });

                if (hasInheritedItems) {
                    children.forEach((child, index) => {
                        if (child.text && isExpanded) {
                            dropdownOptions.push({
                                id: child.key,
                                text: child.text,
                                data: { isInheritedItem: true }
                            });
                        }
                    });
                }
            }
        });

        return dropdownOptions;
    };
}
